//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useState } from 'react'
import { Box, Modal, Typography, TextField, Button, IconButton, Paper } from '@mui/material'
import { Close as CloseIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { companyProfilePut } from '@react/fetchers/companyProfileFetchers'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { chainActions } from '@react/redux/chainSlice'
import { logOT } from '@shared/loggingShared'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { onPromise } from '@react/components/UiHelpers'

interface EditWalletsModalProps {
  open: boolean
  onClose: () => void
  companyProfile: CompanyProfileCTO
  onSuccess?: (updatedProfile: CompanyProfileCTO) => void
}

// Schema for wallet editing
const EditWalletsSchema = yup.object({
  wallets: yup
    .array()
    .of(
      yup
        .string()
        .matches(/^0x[a-fA-F0-9]{40}$/, 'Please enter a valid Ethereum address')
        .required('Wallet address is required')
    )
    .min(0, 'No minimum required'),
})

type EditWalletsFormData = yup.InferType<typeof EditWalletsSchema>

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0px 16px 50px rgba(48, 49, 51, 0.08), 0px 0px 0.5px #303133',
  p: 4,
}

export default function EditWalletsModal({
  open,
  onClose,
  companyProfile,
  onSuccess,
}: EditWalletsModalProps): ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    cognitoUser,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()
  const dispatch = useOtDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<EditWalletsFormData>({
    resolver: yupResolver(EditWalletsSchema),
    defaultValues: {
      wallets: companyProfile.wallets,
    },
  })

  // Watch wallets to handle dynamic wallet fields
  const watchedWallets = watch('wallets')

  // Reset form when companyProfile changes
  React.useEffect(() => {
    reset({
      wallets: companyProfile.wallets,
    })
  }, [companyProfile, reset])

  const onErrors = (errors: any) => {
    logOT.info('ERRORS: ', errors)
  }

  const onSubmit: SubmitHandler<EditWalletsFormData> = async (data) => {
    if (!serverApiBaseUrl && !(cognitoUser || location.hostname === 'localhost')) {
      logOT.error('Missing required configuration for saving company profile')
      return
    }

    setIsSubmitting(true)
    try {
      const updatedProfile: CompanyProfileCTO = {
        ...companyProfile,
        wallets: data.wallets?.filter((wallet) => wallet.trim() !== '') || [],
      }

      const savedProfile = await companyProfilePut(serverApiBaseUrl, updatedProfile, restAPIKey, cognitoUser!)
      dispatch(chainActions.updateCompanyProfile(savedProfile))

      onSuccess?.(savedProfile)
      onClose()
    } catch (error) {
      logOT.error('Error saving company profile wallets:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddWallet = () => {
    const currentWallets = watch('wallets') || []
    setValue('wallets', [...currentWallets, ''])
  }

  const handleRemoveWallet = (index: number) => {
    const currentWallets = watch('wallets') || []
    const newWallets = currentWallets.filter((_, i) => i !== index)
    setValue('wallets', newWallets)
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper sx={modalStyle}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Connected Wallets
          </Typography>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={onPromise(handleSubmit(onSubmit, onErrors))}>
          {/* Wallet Addresses */}
          <Box display="flex" flexDirection="column" gap={2} mb={3}>
            {(watchedWallets || []).map((_wallet, index) => (
              <Box key={index} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  {...register(`wallets.${index}` as const)}
                  variant="outlined"
                  size="small"
                  placeholder="Enter wallet address (0x...)"
                  error={!!errors.wallets?.[index]}
                  helperText={errors.wallets?.[index]?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveWallet(index)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    '&:hover': {
                      backgroundColor: '#d0d0d0',
                    },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="text"
              onClick={handleAddWallet}
              sx={{
                alignSelf: 'flex-start',
                mt: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                color: '#000',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              ADD WALLET
            </Button>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Modal>
  )
}
